import { Heading } from "@components/atoms/Heading"
import {
  ChartContainer,
  FloatingFilter,
  FuelTypeFilter,
  Switch,
  SwitchRow,
  SwitchSelection,
  TickDate,
  Timeline,
  TimelineSelection,
  states,
} from "@components/atoms/data/Components"
import { Col, Grid } from "@components/layout/Grid"
import { EditableText, defineBlock } from "eddev/blocks"
import { MapContainer, TileLayer } from "react-leaflet"
import L, { LatLng, LatLngExpression, Map } from "leaflet"
import { cn, cx } from "@utils/tw"
import { ALL_FUELS, BEV, CLOSE, HFCEV, HYBRID, ICE, PHEV, SEARCH } from "@components/atoms/Icons"
import "leaflet/dist/leaflet.css"
import {
  useEffect,
  useState,
  useMemo,
  useRef,
  Fragment,
  forwardRef,
  ComponentPropsWithoutRef,
  ComponentProps,
} from "react"
import "leaflet.heat/dist/leaflet-heat.js"
import { useEvRegByPostcode } from "@hooks/queries"
import { useHydrating } from "eddev/routing"
import postcodeCenters from "./postcode-center.json"
import { format, parse } from "date-fns"
import { Typography } from "@components/atoms/Typography"
import { numberWithCommas } from "@utils/numberWithCommas"
import { VSpace } from "@components/layout/Spacer"
import { FileDownload } from "@blocks/general/file-download-group"
import { AnimatePresence, useInView } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Button } from "@components/atoms/Button"
import { ZoomState } from "react-use/lib/usePinchZoom"
import { HeatLatLngTuple } from "leaflet"

export const meta: BlockMeta = {
  title: "EV Index Geographical Distribution",
  category: "common",
  icon: "admin-site-alt2",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

type StatesT = "NSW" | "VIC" | "QLD" | "SA" | "WA" | "TAS" | "NT" | "ACT"

type Coordinate = [number, number]

type Geometry = {
  type: "Point"
  coordinates: Coordinate
}

type Properties = {
  POA_CODE21: string
}

type Feature = {
  type: "Feature"
  geometry: Geometry
  properties: Properties
}

type FuelData = {
  ICE: number
  BEV: number
  "Hybrid/PHEV": number
  HFCEV: number
  total: number
}

type PostcodeData = {
  postcode: number
  state: string
  coordinates: Coordinate
  data: FuelData
}

type DataT = {
  date: string
  postcodes: PostcodeData[]
}[]

// The two blocks below fully restyle the shared FuelTypeFilter atom for this
// block's own markup (buttons classed "all-fuels"/"ice"/"bev"/"hybrid"/"hfcev",
// an li classed "double" for the wider Hybrid/PHEV entry) — ported 1:1 from
// this file's own local stitches `css` overrides. Colors are ported from
// utils/evDataHelpers' `colors` map: BEV/PHEV/HFCEV have no design-token
// equivalent (arbitrary rgb()), Hybrid (rgb(255,237,72)) exactly matches
// alt-yellow-light, and ICE here intentionally uses a 50%-alpha mid-grey
// (colors.ICE_50) rather than the solid mid-grey the shared component's own
// default ".ice" styling uses.
const FUEL_TYPE_FILTER_MOBILE_CLASS = cx(
  "[&_ul]:flex [&_ul]:justify-between [&_ul]:list-none [&_ul]:gap-4 [&_ul]:py-1",
  "[&_ul_li]:flex [&_ul_li]:flex-col [&_ul_li]:items-center [&_ul_li]:justify-center [&_ul_li]:max-w-[40px]",
  "[&_ul_li.double]:max-w-[80px]",
  "[&_ul_li>span]:mt-1",
  "[&_ul_li_button]:w-[40px] [&_ul_li_button]:flex [&_ul_li_button]:flex-col [&_ul_li_button]:items-center [&_ul_li_button]:justify-center [&_ul_li_button]:appearance-none [&_ul_li_button]:border-none [&_ul_li_button]:gap-2 [&_ul_li_button]:bg-transparent [&_ul_li_button]:text-black",
  "[&_ul_li_button_i]:flex [&_ul_li_button_i]:flex-row [&_ul_li_button_i]:items-center [&_ul_li_button_i]:justify-center [&_ul_li_button_i]:w-[40px] [&_ul_li_button_i]:h-[40px] [&_ul_li_button_i]:rounded-[40px] [&_ul_li_button_i]:bg-light-grey [&_ul_li_button_i]:my-0 [&_ul_li_button_i]:py-0 [&_ul_li_button_i]:transition-all [&_ul_li_button_i]:duration-300 [&_ul_li_button_i]:ease-movement",
  "[&_ul_li_button_i_svg]:w-[20px] [&_ul_li_button_i_svg]:h-[20px]",
  "[&_ul_li_button_span]:type-label-s [&_ul_li_button_span]:text-mid-grey [&_ul_li_button_span]:whitespace-nowrap",
  "[&_ul_li_button.hybrid]:w-[80px] [&_ul_li_button.hybrid_i]:w-[80px] [&_ul_li_button.hybrid_i]:gap-2",
  "[&_ul_li_button.active.all-fuels_i]:bg-yellow",
  "[&_ul_li_button.active.ice_i]:bg-mid-grey/50",
  "[&_ul_li_button.active.bev_i]:bg-[rgb(96,124,222)]",
  "[&_ul_li_button.active.phev_i]:bg-[rgb(255,147,68)]",
  "[&_ul_li_button.active.hybrid_i]:bg-alt-yellow-light",
  "[&_ul_li_button.active.hfcev_i]:bg-[rgb(151,229,245)]",
)

const FUEL_TYPE_FILTER_DESKTOP_CLASS = cx(
  "[&_ul]:flex [&_ul]:justify-center [&_ul]:list-none [&_ul]:gap-4 [&_ul]:py-6",
  "[&_ul_li]:flex [&_ul_li]:flex-col [&_ul_li]:items-center [&_ul_li]:justify-center [&_ul_li]:w-[90px]",
  "[&_ul_li.double]:w-[130px]",
  "[&_ul_li>span]:mt-1",
  "[&_ul_li_button]:w-[60px] [&_ul_li_button]:flex [&_ul_li_button]:flex-col [&_ul_li_button]:items-center [&_ul_li_button]:justify-center [&_ul_li_button]:appearance-none [&_ul_li_button]:border-none [&_ul_li_button]:gap-2 [&_ul_li_button]:bg-transparent [&_ul_li_button]:text-black",
  "[&_ul_li_button_i]:flex [&_ul_li_button_i]:flex-row [&_ul_li_button_i]:items-center [&_ul_li_button_i]:justify-center [&_ul_li_button_i]:w-[60px] [&_ul_li_button_i]:h-[60px] [&_ul_li_button_i]:rounded-[60px] [&_ul_li_button_i]:bg-light-grey [&_ul_li_button_i]:my-0 [&_ul_li_button_i]:py-0 [&_ul_li_button_i]:transition-all [&_ul_li_button_i]:duration-300 [&_ul_li_button_i]:ease-movement",
  "[&_ul_li_button_i_svg]:w-[40px] [&_ul_li_button_i_svg]:h-[40px]",
  "[&_ul_li_button_span]:type-label-s [&_ul_li_button_span]:text-mid-grey",
  "[&_ul_li_button.hybrid]:w-[calc(120px+var(--spacing-2))] [&_ul_li_button.hybrid_i]:w-[calc(120px+var(--spacing-2))] [&_ul_li_button.hybrid_i]:gap-2",
  "[&_ul_li_button.active.all-fuels_i]:bg-yellow",
  "[&_ul_li_button.active.ice_i]:bg-mid-grey/50",
  "[&_ul_li_button.active.bev_i]:bg-[rgb(96,124,222)]",
  "[&_ul_li_button.active.phev_i]:bg-[rgb(255,147,68)]",
  "[&_ul_li_button.active.hybrid_i]:bg-alt-yellow-light",
  "[&_ul_li_button.active.hfcev_i]:bg-[rgb(151,229,245)]",
)

export default defineBlock("data/evindex-geographical-distribution", (props) => {
  const isMobile = useIsMobile()

  const coordinates = {
    "All states": {
      center: [-26.509905, 134.620161] as LatLngExpression,
      zoom: 5,
      mob: 3,
    },
    NSW: {
      center: [-31.253218, 146.921097] as LatLngExpression,
      zoom: 7,
      mob: 5,
    },
    VIC: {
      center: [-37.011189, 144.894907] as LatLngExpression,
      zoom: 7,
      mob: 5,
    },
    QLD: {
      center: [-22.813213, 143.277492] as LatLngExpression,
      zoom: 5,
      mob: 4,
    },
    SA: {
      center: [-30.345028, 134.725108] as LatLngExpression,
      zoom: 6,
      mob: 5,
    },
    WA: {
      center: [-25.212396, 121.933293] as LatLngExpression,
      zoom: 5,
      mob: 4,
    },
    TAS: {
      center: [-42.201819, 146.747367] as LatLngExpression,
      zoom: 7,
      mob: 6,
    },
    NT: {
      center: [-19.407669, 133.419611] as LatLngExpression,
      zoom: 6,
      mob: 5,
    },
    ACT: {
      center: [-35.495914, 148.978473] as LatLngExpression,
      zoom: 9,
      mob: 8,
    },
  }

  const postcodeData = useEvRegByPostcode(undefined)
  const hydrating = useHydrating()
  const [map, setMap] = useState<Map | null>(null)
  const [currentFuelType, setCurrentFuelType] = useState<"All fuels" | "ICE" | "BEV" | "Hybrid/PHEV" | "HFCEV">(
    "All fuels",
  )
  const [currentLocation, setCurrentLocation] = useState<LatLngExpression>(coordinates["All states"].center)
  const [currentZoom, setCurrentZoom] = useState(
    isMobile ? coordinates["All states"].mob : coordinates["All states"].zoom,
  )
  const [currentPeriod, setCurrentPeriod] = useState<number>(0)
  const [currentState, setCurrentState] = useState<keyof typeof coordinates>("All states")
  const [postcodeSearch, setPostcodeSearch] = useState<string | null>(null)

  function transformAndIntegrateData(rawData: any[], geoData: Feature[]): DataT {
    // Create a map of coordinates by postcode
    const coordinatesMap: Record<number, Coordinate> = {}

    geoData.forEach((feature) => {
      const postcode = parseInt(feature.properties.POA_CODE21)
      coordinatesMap[postcode] = feature.geometry.coordinates
    })

    // Create a map to aggregate data by date
    const dateMap: Record<string, Record<number, FuelData>> = {}

    // Iterate through each entry in the raw data
    rawData.forEach((entry) => {
      Object.keys(entry).forEach((key) => {
        if (key.includes("-Jan-")) {
          // Check if the key is a date
          if (!dateMap[key]) {
            dateMap[key] = {}
          }

          const postcode = entry.Postcode

          if (!dateMap[key][postcode]) {
            dateMap[key][postcode] = {
              ICE: 0,
              BEV: 0,
              "Hybrid/PHEV": 0,
              HFCEV: 0,
              total: 0,
            }
          }

          const fuelType = entry["Fuel Type"] as keyof FuelData
          dateMap[key][postcode][fuelType] += entry[key]
          dateMap[key][postcode].total += entry[key]
        }
      })
    })

    // Convert the map into the desired array format
    const result: DataT = Object.keys(dateMap)
      .map((date) => ({
        date,
        postcodes: Object.keys(dateMap[date]).map((postcode) => ({
          postcode: parseInt(postcode),
          state: rawData.filter((data) => data.Postcode === parseInt(postcode))[0]["State"],
          coordinates: coordinatesMap[parseInt(postcode)] || [0, 0],
          data: dateMap[date][parseInt(postcode)],
        })),
      }))
      .sort(
        (a, b) =>
          parse(a.date, "dd-MMM-yyyy", new Date()).getTime() - parse(b.date, "dd-MMM-yyyy", new Date()).getTime(),
      )

    return result
  }

  const parsedData = useMemo(() => {
    if (postcodeData.data?.evRegByPostcode) {
      const sorted = postcodeData.data.evRegByPostcode
        .map((data) => JSON.parse(data?.data ?? ""))
        .sort((a, b) => (a.Postcode > b.Postcode ? 1 : b.Postcode > a.Postcode ? -1 : 0))

      return transformAndIntegrateData(sorted, postcodeCenters.features as Feature[])
    }
    return []
  }, [postcodeData.data?.evRegByPostcode])

  const dates = useMemo(() => {
    if (parsedData.length > 0) return parsedData.map((entry) => entry.date)
    return []
  }, [parsedData])

  useEffect(() => {
    if (dates.length > 0) setCurrentPeriod(dates.length - 1)
  }, [dates])

  useEffect(() => {
    if (map && parsedData.length > 0 && dates.length > 0) {
      // Remove all layers except the base layer
      map.eachLayer(function (layer) {
        //@ts-ignore
        if (layer.options.id !== "mapbox.streets") map.removeLayer(layer)
      })

      // Get data for the current period
      const date = parsedData.find((d) => d.date === dates[currentPeriod])?.postcodes || []

      // Define radius ranges and corresponding radii
      const radiusRanges = ["All fuels", "ICE"].includes(currentFuelType)
        ? [
            { min: 0, max: 500, radius: 0.5 },
            { min: 500, max: 1000, radius: 1 },
            { min: 1000, max: 2000, radius: 2 },
            { min: 2000, max: 4000, radius: 4 },
            { min: 4000, max: 10000, radius: 10 },
            { min: 10000, max: 20000, radius: 20 },
            { min: 20000, max: 60000, radius: 30 }, // Max value adjusted to cover the highest range
          ]
        : ["Hybrid/PHEV"].includes(currentFuelType)
          ? [
              { min: 0, max: 2000, radius: 15 },
              { min: 2000, max: 4000, radius: 20 },
              { min: 4000, max: 10000, radius: 25 },
              { min: 10000, max: 40000, radius: 30 },
            ]
          : [
              { min: 0, max: 2000, radius: 24 },
              { min: 2000, max: 4000, radius: 26 },
              { min: 4000, max: 10000, radius: 28 },
              { min: 10000, max: 30000, radius: 30 },
            ]

      // Create a heat layer for each range
      radiusRanges.forEach(({ min, max, radius }) => {
        const pointsInRange: HeatLatLngTuple[] = date
          .filter((data) => {
            const value = data.data[currentFuelType === "All fuels" ? "total" : currentFuelType]
            return value >= min && value < max
          })
          .map((data) => [
            data.coordinates[1],
            data.coordinates[0],
            data.data[currentFuelType === "All fuels" ? "total" : currentFuelType],
          ])

        if (pointsInRange.length > 0) {
          // Add the heat layer
          L.heatLayer(pointsInRange, {
            radius: radius * (currentZoom / 20), // Apply gradual scaling
            max: 1, // Lower the max intensity to reduce red color
            blur: ["All fuels", "ICE"].includes(currentFuelType)
              ? 10
              : ["Hybrid/PHEV"].includes(currentFuelType)
                ? 5
                : 2, // Adjust blur for smoother heatmap
            gradient: {
              0: "rgba(33,102,172,0)", // Transparent blue
              0.1: "rgba(220,226,230,1)", // Light gray-blue
              0.15: "rgba(143,183,215,1)", // Soft blue
              0.4: "rgba(205,238,234,1)", // Lighter teal
              0.5: "rgba(213,216,22,1)", // Yellow-green
              0.8: "rgba(247,251,12,1)", // Bright yellow
              1: "rgba(222,23,23,1)", // Red
            },
          }).addTo(map)

          if (currentZoom > 10) {
            // Add circle markers for hover interaction
            pointsInRange.forEach((tuple) => {
              const circleMarker = L.circleMarker([tuple[0], tuple[1]], {
                radius: 20,
                color: "transparent",
                weight: 1,
                fillColor: "transparent",
                fillOpacity: 0,
              }).addTo(map)

              // Add hover interactions
              circleMarker.on("mouseover", () => {
                circleMarker.setStyle({
                  radius: 20,
                  fillColor: "yellow",
                  fillOpacity: 0.8,
                })
              })

              circleMarker.on("click", () => {
                const postcode = date.filter(
                  (data) => data.coordinates[1] === tuple[0] && data.coordinates[0] === tuple[1],
                )[0].postcode
                if (postcode) {
                  searchPostcode(postcode.toString(), false)
                }
              })

              circleMarker.on("mouseout", () => {
                circleMarker.setStyle({
                  fillColor: "transparent",
                  fillOpacity: 0,
                })
              })
            })
          }
        }
      })
    }
  }, [map, parsedData, dates, currentPeriod, currentFuelType, currentZoom])

  const changeMap = (location: keyof typeof coordinates, element: Element) => {
    setPostcodeSearch(null)
    if (map) {
      setCurrentState(location)
      map.setView(
        coordinates[location].center,
        isMobile ? coordinates[location].mob : coordinates[location].zoom,
        undefined,
      )
    }
  }

  const getTotals = (fuelType: "total" | "ICE" | "BEV" | "Hybrid/PHEV" | "HFCEV") => {
    if (currentState === "All states") {
      return numberWithCommas(
        parsedData
          .filter((data) => data.date === dates[currentPeriod])[0]
          .postcodes.reduce((a, b) => a + b.data[fuelType], 0),
      )
    }

    return numberWithCommas(
      parsedData
        .filter((data) => data.date === dates[currentPeriod])[0]
        .postcodes.filter((data) => data.state === currentState)
        .reduce((a, b) => a + b.data[fuelType], 0),
    )
  }

  const getPostcodeTotals = (fuelType: "total" | "ICE" | "BEV" | "Hybrid/PHEV" | "HFCEV", postcode: string) => {
    return numberWithCommas(
      parsedData
        .filter((data) => data.date === dates[currentPeriod])[0]
        .postcodes.filter((data) => data.postcode === parseInt(postcode))[0].data[fuelType],
    )
  }

  const postcodeInputRef = useRef<HTMLInputElement>(null)
  const searchPostcode = (pc: string, zoom: boolean = true) => {
    if (postcodeCenters.features.some((a) => a.properties.POA_CODE21 === pc)) {
      setPostcodeSearch(pc)
      const data = parsedData
        .filter((data) => data.date === dates[currentPeriod])[0]
        .postcodes.filter((data) => data.postcode === parseInt(pc))[0]

      // set map coords and zoom
      if (map) {
        setCurrentState(data.state as keyof typeof coordinates)
        if (zoom) {
          const coordinates: LatLngExpression = [data.coordinates[1], data.coordinates[0]]

          // If mobile, just set the view directly
          map.setView(coordinates, 16)
          if (!isMobile) {
            // Calculate offset for desktop
            const mapSize = map.getSize()
            const offsetX = mapSize.x * 0.25 // 25% offset from the center

            // Convert the pixel offset to lat/lng difference
            const point = map.latLngToContainerPoint(coordinates)
            const newPoint = L.point(point.x - offsetX, point.y)
            const newCoordinates = map.containerPointToLatLng(newPoint)

            setTimeout(() => {
              map.panTo(newCoordinates)
            }, 1000)
          }
        }
      }
    }
  }

  const [description, setDescription] = useState<{
    title: string
    description: string
  }>({
    title: props.titleAustralia ?? "",
    description: props.descriptionAustralia ?? "",
  })
  const getDescriptions = () => {
    let title = ""
    let description = ""

    switch (currentState) {
      case "All states":
        title = props.titleAustralia ?? ""
        description = props.descriptionAustralia ?? ""
        break
      case "NSW":
        title = props.titleNsw ?? ""
        description = props.descriptionNsw ?? ""
        break
      case "VIC":
        title = props.titleVic ?? ""
        description = props.descriptionVic ?? ""
        break
      case "QLD":
        title = props.titleQld ?? ""
        description = props.descriptionQld ?? ""
        break
      case "SA":
        title = props.titleSa ?? ""
        description = props.descriptionSa ?? ""
        break
      case "WA":
        title = props.titleWa ?? ""
        description = props.descriptionWa ?? ""
        break
      case "TAS":
        title = props.titleTas ?? ""
        description = props.descriptionTas ?? ""
        break
      case "NT":
        title = props.titleNt ?? ""
        description = props.descriptionNt ?? ""
        break
      case "ACT":
        title = props.titleAct ?? ""
        description = props.descriptionAct ?? ""
        break
    }

    setDescription({
      title: title,
      description: description,
    })
  }
  useEffect(() => {
    getDescriptions()
  }, [currentState])

  function getStateDetails(state: StatesT) {
    const data = parsedData.filter((data) => data.date === dates[currentPeriod])[0].postcodes
    // Filter the data to include only entries from the specified state
    const stateData = data.filter((entry) => entry.state === state)
    const totalRegistrations = stateData.reduce((acc, entry) => acc + entry.data.total, 0)

    // Calculate total BEV registrations for the state
    const totalBEVRegistrations = stateData.reduce((acc, entry) => acc + entry.data.BEV, 0)

    // Calculate BEV registration penetration for the state
    const BEVPenetration = totalRegistrations > 0 ? (totalBEVRegistrations / totalRegistrations) * 100 : 0

    // Calculate BEV registrations and penetration for all states
    const stateBEVData = data.reduce((acc: Record<string, FuelData>, entry) => {
      acc[entry.state] = acc[entry.state] || { BEV: 0, ICE: 0, "Hybrid/PHEV": 0, HFCEV: 0, total: 0 }
      acc[entry.state].BEV += entry.data.BEV
      acc[entry.state].total += entry.data.total
      return acc
    }, {})

    const BEVstateRanks = Object.entries(stateBEVData)
      .sort((a, b) => b[1].BEV - a[1].BEV)
      .map(([state, data], index) => ({ state, BEV: data.BEV, rank: index + 1 }))

    const BEVstatePenetrationRanks = Object.entries(stateBEVData)
      .sort((a, b) => b[1].BEV / b[1].total - a[1].BEV / a[1].total)
      .map(([state, data], index) => ({ state, penetration: (data.BEV / data.total) * 100, rank: index + 1 }))

    const BEVStateRank = BEVstateRanks.find((rank) => rank.state === state)?.rank || 0
    const BEVPenetrationStateRank = BEVstatePenetrationRanks.find((rank) => rank.state === state)?.rank || 0

    // Calculate total ICE registrations for the state
    const totalICERegistrations = stateData.reduce((acc, entry) => acc + entry.data.ICE, 0)

    // Calculate ICE registration penetration for the state
    const ICEPenetration = totalRegistrations > 0 ? (totalICERegistrations / totalRegistrations) * 100 : 0

    // Calculate ICE registrations and penetration for all states
    const stateICEData = data.reduce((acc: Record<string, FuelData>, entry) => {
      acc[entry.state] = acc[entry.state] || { BEV: 0, ICE: 0, "Hybrid/PHEV": 0, HFCEV: 0, total: 0 }
      acc[entry.state].ICE += entry.data.ICE
      acc[entry.state].total += entry.data.total
      return acc
    }, {})

    const ICEstateRanks = Object.entries(stateICEData)
      .sort((a, b) => b[1].ICE - a[1].ICE)
      .map(([state, data], index) => ({ state, ICE: data.ICE, rank: index + 1 }))

    const ICEstatePenetrationRanks = Object.entries(stateICEData)
      .sort((a, b) => b[1].ICE / b[1].total - a[1].ICE / a[1].total)
      .map(([state, data], index) => ({ state, penetration: (data.ICE / data.total) * 100, rank: index + 1 }))

    const ICEStateRank = ICEstateRanks.find((rank) => rank.state === state)?.rank || 0
    const ICEPenetrationStateRank = ICEstatePenetrationRanks.find((rank) => rank.state === state)?.rank || 0

    // Calculate total HFCEV registrations for the state
    const totalHFCEVRegistrations = stateData.reduce((acc, entry) => acc + entry.data.HFCEV, 0)

    // Calculate HFCEV registration penetration for the state
    const HFCEVPenetration = totalRegistrations > 0 ? (totalHFCEVRegistrations / totalRegistrations) * 100 : 0

    // Calculate HFCEV registrations and penetration for all states
    const stateHFCEVData = data.reduce((acc: Record<string, FuelData>, entry) => {
      acc[entry.state] = acc[entry.state] || { BEV: 0, ICE: 0, "Hybrid/PHEV": 0, HFCEV: 0, total: 0 }
      acc[entry.state].HFCEV += entry.data.HFCEV
      acc[entry.state].total += entry.data.total
      return acc
    }, {})

    const HFCEVstateRanks = Object.entries(stateHFCEVData)
      .sort((a, b) => b[1].HFCEV - a[1].HFCEV)
      .map(([state, data], index) => ({ state, HFCEV: data.HFCEV, rank: index + 1 }))

    const HFCEVstatePenetrationRanks = Object.entries(stateHFCEVData)
      .sort((a, b) => b[1].HFCEV / b[1].total - a[1].HFCEV / a[1].total)
      .map(([state, data], index) => ({ state, penetration: (data.HFCEV / data.total) * 100, rank: index + 1 }))

    const HFCEVStateRank = HFCEVstateRanks.find((rank) => rank.state === state)?.rank || 0
    const HFCEVPenetrationStateRank = HFCEVstatePenetrationRanks.find((rank) => rank.state === state)?.rank || 0

    // Calculate total HybridPHEV registrations for the state
    const totalHybridPHEVRegistrations = stateData.reduce((acc, entry) => acc + entry.data["Hybrid/PHEV"], 0)

    // Calculate HybridPHEV registration penetration for the state
    const HybridPHEVPenetration = totalRegistrations > 0 ? (totalHybridPHEVRegistrations / totalRegistrations) * 100 : 0

    // Calculate HybridPHEV registrations and penetration for all states
    const stateHybridPHEVData = data.reduce((acc: Record<string, FuelData>, entry) => {
      acc[entry.state] = acc[entry.state] || { BEV: 0, ICE: 0, "Hybrid/PHEV": 0, HybridPHEV: 0, total: 0 }
      acc[entry.state]["Hybrid/PHEV"] += entry.data["Hybrid/PHEV"]
      acc[entry.state].total += entry.data.total
      return acc
    }, {})

    const HybridPHEVstateRanks = Object.entries(stateHybridPHEVData)
      .sort((a, b) => b[1]["Hybrid/PHEV"] - a[1]["Hybrid/PHEV"])
      .map(([state, data], index) => ({ state, HybridPHEV: data["Hybrid/PHEV"], rank: index + 1 }))

    const HybridPHEVstatePenetrationRanks = Object.entries(stateHybridPHEVData)
      .sort((a, b) => b[1]["Hybrid/PHEV"] / b[1].total - a[1]["Hybrid/PHEV"] / a[1].total)
      .map(([state, data], index) => ({
        state,
        penetration: (data["Hybrid/PHEV"] / data.total) * 100,
        rank: index + 1,
      }))

    const HybridPHEVStateRank = HybridPHEVstateRanks.find((rank) => rank.state === state)?.rank || 0
    const HybridPHEVPenetrationStateRank =
      HybridPHEVstatePenetrationRanks.find((rank) => rank.state === state)?.rank || 0

    // Calculate total All fuels registrations for the state
    const totalAllRegistrations = stateData.reduce((acc, entry) => acc + entry.data.total, 0)

    // Calculate All fuels registration penetration for the state
    const AllPenetration = totalRegistrations > 0 ? (totalAllRegistrations / totalRegistrations) * 100 : 0

    // Calculate All fuels registrations and penetration for all states
    const stateAllData = data.reduce((acc: Record<string, FuelData>, entry) => {
      acc[entry.state] = acc[entry.state] || { BEV: 0, ICE: 0, "Hybrid/PHEV": 0, All: 0, total: 0 }
      acc[entry.state].total += entry.data.total
      acc[entry.state].total += entry.data.total
      return acc
    }, {})

    const AllstateRanks = Object.entries(stateAllData)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([state, data], index) => ({ state, All: data.total, rank: index + 1 }))

    const AllstatePenetrationRanks = Object.entries(stateAllData)
      .sort((a, b) => b[1].total / b[1].total - a[1].total / a[1].total)
      .map(([state, data], index) => ({ state, penetration: (data.total / data.total) * 100, rank: index + 1 }))

    const AllStateRank = AllstateRanks.find((rank) => rank.state === state)?.rank || 0
    const AllPenetrationStateRank = AllstatePenetrationRanks.find((rank) => rank.state === state)?.rank || 0

    return {
      state,
      totalBEVRegistrations,
      BEVStateRank,
      BEVPenetration,
      BEVPenetrationStateRank,

      totalICERegistrations,
      ICEStateRank,
      ICEPenetration,
      ICEPenetrationStateRank,

      totalHybridPHEVRegistrations,
      HybridPHEVStateRank,
      HybridPHEVPenetration,
      HybridPHEVPenetrationStateRank,

      totalHFCEVRegistrations,
      HFCEVStateRank,
      HFCEVPenetration,
      HFCEVPenetrationStateRank,

      totalAllRegistrations,
      AllStateRank,
      AllPenetration,
      AllPenetrationStateRank,
    }
  }

  function getPostcodeDetails(postcode: number) {
    const data = parsedData.filter((data) => data.date === dates[currentPeriod])[0].postcodes
    const postcodeData = data.find((entry) => entry.postcode === postcode)

    if (!postcodeData) {
      return null // Postcode not found
    }

    const { state } = postcodeData

    // Calculate postcode BEV registration penetration
    const BEVPenetration = postcodeData.data.total > 0 ? (postcodeData.data.BEV / postcodeData.data.total) * 100 : 0

    // Calculate BEV registrations for all postcodes
    const BEVpostcodeRanks = [...data]
      .sort((a, b) => b.data.BEV - a.data.BEV)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Calculate BEV penetration for all postcodes
    const BEVpostcodePenetrationRanks = [...data]
      .sort((a, b) => b.data.BEV / b.data.total - a.data.BEV / a.data.total)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Filter ranks by state for state-specific ranks
    const stateBEVPostcodeRanks = BEVpostcodeRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    const stateBEVPostcodePenetrationRanks = BEVpostcodePenetrationRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Get ranks for the specified postcode
    const postcodeBEVRank = BEVpostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeStateBEVRank = stateBEVPostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeBEVPenetrationRank = BEVpostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeBEVStatePenetrationRank =
      stateBEVPostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0

    // Calculate postcode ICE registration penetration
    const ICEPenetration = postcodeData.data.total > 0 ? (postcodeData.data.ICE / postcodeData.data.total) * 100 : 0

    // Calculate ICE registrations for all postcodes
    const ICEpostcodeRanks = [...data]
      .sort((a, b) => b.data.ICE - a.data.ICE)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Calculate ICE penetration for all postcodes
    const ICEpostcodePenetrationRanks = [...data]
      .sort((a, b) => b.data.ICE / b.data.total - a.data.ICE / a.data.total)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Filter ranks by state for state-specific ranks
    const stateICEPostcodeRanks = ICEpostcodeRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    const stateICEPostcodePenetrationRanks = ICEpostcodePenetrationRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Get ranks for the specified postcode
    const postcodeICERank = ICEpostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeStateICERank = stateICEPostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeICEPenetrationRank = ICEpostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeICEStatePenetrationRank =
      stateICEPostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0

    // Calculate postcode HybridPHEV registration penetration
    const HybridPHEVPenetration =
      postcodeData.data.total > 0 ? (postcodeData.data["Hybrid/PHEV"] / postcodeData.data.total) * 100 : 0

    // Calculate HybridPHEV registrations for all postcodes
    const HybridPHEVpostcodeRanks = [...data]
      .sort((a, b) => b.data["Hybrid/PHEV"] - a.data["Hybrid/PHEV"])
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Calculate HybridPHEV penetration for all postcodes
    const HybridPHEVpostcodePenetrationRanks = [...data]
      .sort((a, b) => b.data["Hybrid/PHEV"] / b.data.total - a.data["Hybrid/PHEV"] / a.data.total)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Filter ranks by state for state-specific ranks
    const stateHybridPHEVPostcodeRanks = HybridPHEVpostcodeRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    const stateHybridPHEVPostcodePenetrationRanks = HybridPHEVpostcodePenetrationRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Get ranks for the specified postcode
    const postcodeHybridPHEVRank = HybridPHEVpostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeStateHybridPHEVRank =
      stateHybridPHEVPostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeHybridPHEVPenetrationRank =
      HybridPHEVpostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeHybridPHEVStatePenetrationRank =
      stateHybridPHEVPostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0

    // Calculate postcode HFCEV registration penetration
    const HFCEVPenetration = postcodeData.data.total > 0 ? (postcodeData.data.HFCEV / postcodeData.data.total) * 100 : 0

    // Calculate HFCEV registrations for all postcodes
    const HFCEVpostcodeRanks = [...data]
      .sort((a, b) => b.data.HFCEV - a.data.HFCEV)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Calculate HFCEV penetration for all postcodes
    const HFCEVpostcodePenetrationRanks = [...data]
      .sort((a, b) => b.data.HFCEV / b.data.total - a.data.HFCEV / a.data.total)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Filter ranks by state for state-specific ranks
    const stateHFCEVPostcodeRanks = HFCEVpostcodeRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    const stateHFCEVPostcodePenetrationRanks = HFCEVpostcodePenetrationRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Get ranks for the specified postcode
    const postcodeHFCEVRank = HFCEVpostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeStateHFCEVRank = stateHFCEVPostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeHFCEVPenetrationRank =
      HFCEVpostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeHFCEVStatePenetrationRank =
      stateHFCEVPostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0

    // Calculate postcode All fuels registration penetration
    const AllPenetration = postcodeData.data.total > 0 ? (postcodeData.data.total / postcodeData.data.total) * 100 : 0

    // Calculate All fuels registrations for all postcodes
    const AllpostcodeRanks = [...data]
      .sort((a, b) => b.data.total - a.data.total)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Calculate All penetration for all postcodes
    const AllpostcodePenetrationRanks = [...data]
      .sort((a, b) => b.data.total / b.data.total - a.data.total / a.data.total)
      .map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Filter ranks by state for state-specific ranks
    const stateAllPostcodeRanks = AllpostcodeRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    const stateAllPostcodePenetrationRanks = AllpostcodePenetrationRanks.filter(
      (entry) => data.find((e) => e.postcode === entry.postcode)?.state === state,
    ).map((entry, index) => ({ postcode: entry.postcode, rank: index + 1 }))

    // Get ranks for the specified postcode
    const postcodeAllRank = AllpostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeStateAllRank = stateAllPostcodeRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeAllPenetrationRank = AllpostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0
    const postcodeAllStatePenetrationRank =
      stateAllPostcodePenetrationRanks.find((rank) => rank.postcode === postcode)?.rank || 0

    // Totals
    const totalPostcodes = data.length
    const totalStatePostcodes = stateBEVPostcodeRanks.length

    return {
      postcode,
      state,

      BEVRegistrations: postcodeData.data.BEV,
      postcodeBEVRank: `${postcodeBEVRank} out of ${totalPostcodes}`,
      postcodeStateBEVRank: `${postcodeStateBEVRank} out of ${totalStatePostcodes}`,
      BEVPenetration,
      postcodeBEVPenetrationRank: `${postcodeBEVPenetrationRank} out of ${totalPostcodes}`,
      postcodeBEVStatePenetrationRank: `${postcodeBEVStatePenetrationRank} out of ${totalStatePostcodes}`,

      ICERegistrations: postcodeData.data.ICE,
      postcodeICERank: `${postcodeICERank} out of ${totalPostcodes}`,
      postcodeStateICERank: `${postcodeStateICERank} out of ${totalStatePostcodes}`,
      ICEPenetration,
      postcodeICEPenetrationRank: `${postcodeICEPenetrationRank} out of ${totalPostcodes}`,
      postcodeICEStatePenetrationRank: `${postcodeICEStatePenetrationRank} out of ${totalStatePostcodes}`,

      HybridPHEVRegistrations: postcodeData.data["Hybrid/PHEV"],
      postcodeHybridPHEVRank: `${postcodeHybridPHEVRank} out of ${totalPostcodes}`,
      postcodeStateHybridPHEVRank: `${postcodeStateHybridPHEVRank} out of ${totalStatePostcodes}`,
      HybridPHEVPenetration,
      postcodeHybridPHEVPenetrationRank: `${postcodeHybridPHEVPenetrationRank} out of ${totalPostcodes}`,
      postcodeHybridPHEVStatePenetrationRank: `${postcodeHybridPHEVStatePenetrationRank} out of ${totalStatePostcodes}`,

      HFCEVRegistrations: postcodeData.data.HFCEV,
      postcodeHFCEVRank: `${postcodeHFCEVRank} out of ${totalPostcodes}`,
      postcodeStateHFCEVRank: `${postcodeStateHFCEVRank} out of ${totalStatePostcodes}`,
      HFCEVPenetration,
      postcodeHFCEVPenetrationRank: `${postcodeHFCEVPenetrationRank} out of ${totalPostcodes}`,
      postcodeHFCEVStatePenetrationRank: `${postcodeHFCEVStatePenetrationRank} out of ${totalStatePostcodes}`,

      AllRegistrations: postcodeData.data.total,
      postcodeAllRank: `${postcodeAllRank} out of ${totalPostcodes}`,
      postcodeStateAllRank: `${postcodeStateAllRank} out of ${totalStatePostcodes}`,
      AllPenetration,
      postcodeAllPenetrationRank: `${postcodeAllPenetrationRank} out of ${totalPostcodes}`,
      postcodeAllStatePenetrationRank: `${postcodeAllStatePenetrationRank} out of ${totalStatePostcodes}`,
    }
  }
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: isMobile ? 0.25 : 0.5 })

  useEffect(() => {
    if (map) {
      const handleZoomChange = () => {
        const zoomLevel = map.getZoom()
        setCurrentZoom(zoomLevel)
      }
      map.on("zoomend", handleZoomChange)

      return () => {
        map.off("zoomend", handleZoomChange)
      }
    }
  }, [map])

  return (
    <ChartContainer ref={blockRef}>
      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
        <Col initial={12} laptop={6}>
          <EditableText as="p" placeholder="Enter content..." store="content" />
        </Col>

        {isMobile && (
          <AnimatePresence>
            {inView && (
              <FloatingFilter open={filterOpen} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Button
                  size="tiny"
                  color="yellow"
                  icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                  iconStyle="right"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  Filter this data
                </Button>

                <FilterBlock className="w-full mb-3">
                  <Switch
                    className="w-full"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (postcodeInputRef.current?.value.length === 4) searchPostcode(postcodeInputRef.current.value)
                    }}
                  >
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="all"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) changeMap("All states", e.currentTarget)
                              }}
                            />
                            All states
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                      <SwitchRow border>
                        {Object.entries(states).map(([i, state]) => {
                          if (i === "all") return <Fragment key={i}></Fragment>
                          return (
                            <SwitchSelection key={i}>
                              <label>
                                <input
                                  type="radio"
                                  name="states"
                                  value={i}
                                  onChange={(e) => {
                                    if (e.currentTarget.checked)
                                      changeMap(
                                        e.currentTarget.value.toUpperCase() as
                                          | "NSW"
                                          | "VIC"
                                          | "QLD"
                                          | "SA"
                                          | "WA"
                                          | "TAS"
                                          | "NT"
                                          | "ACT"
                                          | "All states",
                                        e.currentTarget,
                                      )
                                  }}
                                />
                                {i.toUpperCase()}
                              </label>
                            </SwitchSelection>
                          )
                        })}
                      </SwitchRow>
                      <SwitchRow border>
                        <SwitchSelection
                          className={cx(
                            "flex-row px-4 py-2",
                            "[&_input]:block [&_input]:border-none [&_input]:p-1 [&_input]:type-label-s [&_input]:grow",
                            "[&_button]:flex [&_button]:items-center [&_button]:ml-2",
                          )}
                        >
                          <input
                            type="text"
                            placeholder="Search Postcode..."
                            minLength={4}
                            maxLength={4}
                            ref={postcodeInputRef}
                          />
                          <button>{SEARCH}</button>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>
                </FilterBlock>

                {!hydrating && dates.length > 0 && (
                  <>
                    <FuelTypeFilter className={FUEL_TYPE_FILTER_MOBILE_CLASS}>
                      <ul>
                        <li>
                          <button
                            className={`${currentFuelType === "All fuels" ? `active` : ``} all-fuels`}
                            onClick={() => setCurrentFuelType("All fuels")}
                          >
                            <i>{ALL_FUELS}</i>
                            <span>All fuels</span>
                          </button>
                          <Typography as="span" variant="text-xs">
                            {getTotals("total")}
                          </Typography>
                        </li>
                        <li>
                          <button
                            className={`${currentFuelType === "ICE" ? `active` : ``} ice`}
                            onClick={() => setCurrentFuelType("ICE")}
                          >
                            <i>{ICE}</i>
                            <span>ICE</span>
                          </button>
                          <Typography as="span" variant="text-xs">
                            {getTotals("ICE")}
                          </Typography>
                        </li>
                        <li>
                          <button
                            className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                            onClick={() => setCurrentFuelType("BEV")}
                          >
                            <i>{BEV}</i>
                            <span>BEV</span>
                          </button>
                          <Typography as="span" variant="text-xs">
                            {getTotals("BEV")}
                          </Typography>
                        </li>

                        <li className="double">
                          <button
                            className={`${currentFuelType === "Hybrid/PHEV" ? `active` : ``} hybrid`}
                            onClick={() => setCurrentFuelType("Hybrid/PHEV")}
                          >
                            <i>
                              {HYBRID}
                              {PHEV}
                            </i>
                            <span>Hybrid/PHEV</span>
                          </button>
                          <Typography as="span" variant="text-xs">
                            {getTotals("Hybrid/PHEV")}
                          </Typography>
                        </li>
                        <li>
                          <button
                            className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                            onClick={() => setCurrentFuelType("HFCEV")}
                          >
                            <i>{HFCEV}</i>
                            <span>HFCEV</span>
                          </button>
                          <Typography as="span" variant="text-xs">
                            {getTotals("HFCEV")}
                          </Typography>
                        </li>
                      </ul>
                    </FuelTypeFilter>
                    <TimelineSelection light>
                      {parsedData.length > 0 && (
                        <Timeline>
                          <ul>
                            {parsedData
                              .map((date) => date.date)
                              .sort()
                              .map((date, i) => (
                                <li key={i}>
                                  <TickDate
                                    style={{
                                      opacity: i === currentPeriod ? 1 : i === 0 ? (currentPeriod === 1 ? 0 : 0.5) : 0,
                                    }}
                                  >
                                    {format(date, "yyyy")}
                                  </TickDate>
                                </li>
                              ))}
                          </ul>
                          <input
                            type="range"
                            min={0}
                            max={parsedData.length - 1}
                            step={1}
                            defaultValue={parsedData.length - 1}
                            list="period-marks"
                            onChange={(e) => setCurrentPeriod(parseInt(e.target.value))}
                          />
                        </Timeline>
                      )}
                    </TimelineSelection>
                  </>
                )}
              </FloatingFilter>
            )}
          </AnimatePresence>
        )}
        {!isMobile && (
          <>
            <Col initial={12} className="flex justify-center">
              <FilterBlock className="mb-6">
                <Switch
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (postcodeInputRef.current?.value.length === 4) searchPostcode(postcodeInputRef.current.value)
                  }}
                >
                  <div>
                    <SwitchRow>
                      <SwitchSelection>
                        <label>
                          <input
                            type="radio"
                            name="states"
                            value="all"
                            defaultChecked
                            onChange={(e) => {
                              if (e.currentTarget.checked) changeMap("All states", e.currentTarget)
                            }}
                          />
                          All states
                        </label>
                      </SwitchSelection>
                    </SwitchRow>
                    <SwitchRow border>
                      {Object.entries(states).map(([i, state]) => {
                        if (i === "all") return <Fragment key={i}></Fragment>
                        return (
                          <SwitchSelection key={i}>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value={i}
                                onChange={(e) => {
                                  if (e.currentTarget.checked)
                                    changeMap(
                                      e.currentTarget.value.toUpperCase() as
                                        | "NSW"
                                        | "VIC"
                                        | "QLD"
                                        | "SA"
                                        | "WA"
                                        | "TAS"
                                        | "NT"
                                        | "ACT"
                                        | "All states",
                                      e.currentTarget,
                                    )
                                }}
                              />
                              {i.toUpperCase()}
                            </label>
                          </SwitchSelection>
                        )
                      })}

                      <SwitchSelection
                        className={cx(
                          "flex-row items-center px-4",
                          "[&_input]:block [&_input]:border-none [&_input]:p-1 [&_input]:type-label-s",
                          "[&_button]:flex [&_button]:items-center [&_button]:ml-2",
                        )}
                      >
                        <input
                          type="text"
                          placeholder="Search Postcode..."
                          minLength={4}
                          maxLength={4}
                          ref={postcodeInputRef}
                        />
                        <button>{SEARCH}</button>
                      </SwitchSelection>
                    </SwitchRow>
                  </div>
                </Switch>
              </FilterBlock>
            </Col>

            <Col initial={12}>
              {!hydrating && dates.length > 0 && (
                <TimelineSelection light>
                  <FuelTypeFilter className={FUEL_TYPE_FILTER_DESKTOP_CLASS}>
                    <ul>
                      <li>
                        <button
                          className={`${currentFuelType === "All fuels" ? `active` : ``} all-fuels`}
                          onClick={() => setCurrentFuelType("All fuels")}
                        >
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                        </button>
                        <Typography as="span" variant="h5">
                          {getTotals("total")}
                        </Typography>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "ICE" ? `active` : ``} ice`}
                          onClick={() => setCurrentFuelType("ICE")}
                        >
                          <i>{ICE}</i>
                          <span>ICE</span>
                        </button>
                        <Typography as="span" variant="h5">
                          {getTotals("ICE")}
                        </Typography>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "BEV" ? `active` : ``} bev`}
                          onClick={() => setCurrentFuelType("BEV")}
                        >
                          <i>{BEV}</i>
                          <span>BEV</span>
                        </button>
                        <Typography as="span" variant="h5">
                          {getTotals("BEV")}
                        </Typography>
                      </li>
                      <li className="double">
                        <button
                          className={`${currentFuelType === "Hybrid/PHEV" ? `active` : ``} hybrid`}
                          onClick={() => setCurrentFuelType("Hybrid/PHEV")}
                        >
                          <i>
                            {HYBRID}
                            {PHEV}
                          </i>
                          <span>Hybrid/PHEV</span>
                        </button>
                        <Typography as="span" variant="h5">
                          {getTotals("Hybrid/PHEV")}
                        </Typography>
                      </li>
                      <li>
                        <button
                          className={`${currentFuelType === "HFCEV" ? `active` : ``} hfcev`}
                          onClick={() => setCurrentFuelType("HFCEV")}
                        >
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                        </button>
                        <Typography as="span" variant="h5">
                          {getTotals("HFCEV")}
                        </Typography>
                      </li>
                    </ul>
                  </FuelTypeFilter>
                  {parsedData.length > 0 && (
                    <Timeline>
                      <ul>
                        {parsedData
                          .map((date) => date.date)
                          .map((date, i) => (
                            <li key={i}>
                              <TickDate
                                style={{
                                  opacity: i === currentPeriod ? 1 : i === 0 ? (currentPeriod === 1 ? 0 : 0.5) : 0,
                                }}
                              >
                                {format(date, "yyyy")}
                              </TickDate>
                            </li>
                          ))}
                      </ul>
                      <input
                        type="range"
                        min={0}
                        max={parsedData.length - 1}
                        step={1}
                        defaultValue={parsedData.length - 1}
                        list="period-marks"
                        onChange={(e) => setCurrentPeriod(parseInt(e.target.value))}
                      />
                    </Timeline>
                  )}
                </TimelineSelection>
              )}
            </Col>
          </>
        )}
        <Col initial={12} className="relative">
          <StyledMapContainer
            scrollWheelZoom={false}
            center={currentLocation}
            zoom={isMobile ? coordinates["All states"].mob : coordinates["All states"].zoom}
            ref={setMap}
          >
            <TileLayer
              url="https://api.mapbox.com/styles/v1/aaa-maps/cllelalck00hf01rdbhdkb7j7/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYWFhLW1hcHMiLCJhIjoiY2thcDNoOWVxMjl1YjMxbG1xcng5dWFrbiJ9.VTtatWklQzVZ-X-l2kg1Gw"
              attribution="Map data &copy; <a href='https://www.openstreetmap.org/'>OpenStreetMap</a><br>Contributors <a href='https://creativecommons.org/licenses/by-sa/2.0/'>CC-BY-SA</a> | Imagery © <a href='https://www.mapbox.com/'>Mapbox</a>"
              maxZoom={20}
              id="mapbox.streets"
              accessToken="pk.eyJ1IjoiYWFhLW1hcHMiLCJhIjoiY2thcDNoOWVxMjl1YjMxbG1xcng5dWFrbiJ9.VTtatWklQzVZ-X-l2kg1Gw"
            />
          </StyledMapContainer>

          {!isMobile && (
            <TableContainer>
              {currentState !== "All states" && (
                <DataTable>
                  <thead>
                    <tr>
                      <th colSpan={5}>
                        {description.title}{" "}
                        <button
                          onClick={() => {
                            setPostcodeSearch(null)
                            setCurrentState("All states")
                          }}
                        >
                          {CLOSE}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                          <Typography as="span" variant="h5">
                            {getTotals("total")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{ICE}</i>
                          <span>ICE</span>
                          <Typography as="span" variant="h5">
                            {getTotals("ICE")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{BEV}</i>
                          <span>BEV</span>
                          <Typography as="span" variant="h5">
                            {getTotals("BEV")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>
                            {HYBRID}
                            {PHEV}
                          </i>
                          <span>Hybrid / PHEV</span>
                          <Typography as="span" variant="h5">
                            {getTotals("Hybrid/PHEV")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                          <Typography as="span" variant="h5">
                            {getTotals("HFCEV")}
                          </Typography>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    {currentFuelType === "ICE" && (
                      <>
                        <tr>
                          <td colSpan={3}>ICE Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalICERegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).ICEStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).ICEPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).ICEPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "BEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>BEV Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalBEVRegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).BEVStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).BEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).BEVPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "Hybrid/PHEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(
                              getStateDetails(description.title as StatesT).totalHybridPHEVRegistrations,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registrations – State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HybridPHEVStateRank} out of 8
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HybridPHEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HybridPHEVPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "HFCEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>HFCEV Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalHFCEVRegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).HFCEVStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HFCEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HFCEVPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "All fuels" && (
                      <>
                        <tr>
                          <td colSpan={3}>All fuels Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalAllRegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).AllStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).AllPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).AllPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </DataTable>
              )}
              {postcodeSearch && (
                <DataTable>
                  <thead>
                    <tr>
                      <th colSpan={5}>
                        Postcode {postcodeSearch} ({description.title}){" "}
                        <button
                          onClick={() => {
                            setPostcodeSearch(null)
                            setCurrentState("All states")
                          }}
                        >
                          {CLOSE}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("total", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{ICE}</i>
                          <span>ICE</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("ICE", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{BEV}</i>
                          <span>BEV</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("BEV", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>
                            {HYBRID}
                            {PHEV}
                          </i>
                          <span>Hybrid / PHEV</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("Hybrid/PHEV", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("HFCEV", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    {currentFuelType === "BEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>BEV Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.BEVRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registrations — Postcode rank in country</td>
                          <td colSpan={2}>{getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeBEVRank}</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateBEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.BEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeBEVPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeBEVStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "ICE" && (
                      <>
                        <tr>
                          <td colSpan={3}>ICE Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.ICERegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registrations — Postcode rank in country</td>
                          <td colSpan={2}>{getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeICERank}</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateICERank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.ICEPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeICEPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeICEStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "Hybrid/PHEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HybridPHEVRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registrations — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHybridPHEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateHybridPHEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HybridPHEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHybridPHEVPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {
                              getPostcodeDetails(parseInt(postcodeSearch as string))
                                ?.postcodeHybridPHEVStatePenetrationRank
                            }
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "HFCEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>HFCEV Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HFCEVRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registrations — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHFCEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateHFCEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HFCEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHFCEVPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHFCEVStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "All fuels" && (
                      <>
                        <tr>
                          <td colSpan={3}>All fuels Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.AllRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registrations — Postcode rank in country</td>
                          <td colSpan={2}>{getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeAllRank}</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateAllRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.AllPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeAllPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeAllStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </DataTable>
              )}
            </TableContainer>
          )}
        </Col>

        {isMobile && (
          <Col initial={12}>
            <TableContainer>
              {currentState !== "All states" && (
                <DataTable>
                  <thead>
                    <tr>
                      <th colSpan={5}>{description.title}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                          <Typography as="span" variant="h5">
                            {getTotals("total")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{ICE}</i>
                          <span>ICE</span>
                          <Typography as="span" variant="h5">
                            {getTotals("ICE")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{BEV}</i>
                          <span>BEV</span>
                          <Typography as="span" variant="h5">
                            {getTotals("BEV")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>
                            {HYBRID}
                            {PHEV}
                          </i>
                          <span>Hybrid / PHEV</span>
                          <Typography as="span" variant="h5">
                            {getTotals("Hybrid/PHEV")}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                          <Typography as="span" variant="h5">
                            {getTotals("HFCEV")}
                          </Typography>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    {currentFuelType === "ICE" && (
                      <>
                        <tr>
                          <td colSpan={3}>ICE Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalICERegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).ICEStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).ICEPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).ICEPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "BEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>BEV Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalBEVRegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).BEVStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).BEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).BEVPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "Hybrid/PHEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(
                              getStateDetails(description.title as StatesT).totalHybridPHEVRegistrations,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registrations – State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HybridPHEVStateRank} out of 8
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HybridPHEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HybridPHEVPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "HFCEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>HFCEV Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalHFCEVRegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).HFCEVStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HFCEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).HFCEVPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "All fuels" && (
                      <>
                        <tr>
                          <td colSpan={3}>All fuels Registrations</td>
                          <td colSpan={2}>
                            {numberWithCommas(getStateDetails(description.title as StatesT).totalAllRegistrations)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registrations – State rank in country</td>
                          <td colSpan={2}>{getStateDetails(description.title as StatesT).AllStateRank} out of 8</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).AllPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration — State rank in country</td>
                          <td colSpan={2}>
                            {getStateDetails(description.title as StatesT).AllPenetrationStateRank} out of 8
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </DataTable>
              )}
              {postcodeSearch && (
                <DataTable>
                  <thead>
                    <tr>
                      <th colSpan={5}>
                        Postcode {postcodeSearch} ({description.title})
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <i>{ALL_FUELS}</i>
                          <span>All fuels</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("total", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{ICE}</i>
                          <span>ICE</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("ICE", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{BEV}</i>
                          <span>BEV</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("BEV", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>
                            {HYBRID}
                            {PHEV}
                          </i>
                          <span>Hybrid / PHEV</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("Hybrid/PHEV", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <div>
                          <i>{HFCEV}</i>
                          <span>HFCEV</span>
                          <Typography as="span" variant="h5">
                            {getPostcodeTotals("HFCEV", postcodeSearch)}
                          </Typography>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    {currentFuelType === "BEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>BEV Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.BEVRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registrations — Postcode rank in country</td>
                          <td colSpan={2}>{getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeBEVRank}</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateBEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.BEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeBEVPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>BEV registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeBEVStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "ICE" && (
                      <>
                        <tr>
                          <td colSpan={3}>ICE Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.ICERegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registrations — Postcode rank in country</td>
                          <td colSpan={2}>{getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeICERank}</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateICERank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.ICEPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeICEPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>ICE registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeICEStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "Hybrid/PHEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HybridPHEVRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registrations — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHybridPHEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateHybridPHEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HybridPHEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHybridPHEVPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>Hybrid/PHEV registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {
                              getPostcodeDetails(parseInt(postcodeSearch as string))
                                ?.postcodeHybridPHEVStatePenetrationRank
                            }
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "HFCEV" && (
                      <>
                        <tr>
                          <td colSpan={3}>HFCEV Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HFCEVRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registrations — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHFCEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateHFCEVRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.HFCEVPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHFCEVPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>HFCEV registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeHFCEVStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                    {currentFuelType === "All fuels" && (
                      <>
                        <tr>
                          <td colSpan={3}>All fuels Registrations</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.AllRegistrations}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registrations — Postcode rank in country</td>
                          <td colSpan={2}>{getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeAllRank}</td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registrations — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeStateAllRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.AllPenetration.toFixed(2)}%
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration — Postcode rank in country</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeAllPenetrationRank}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>All fuels registration penetration — Postcode rank in state</td>
                          <td colSpan={2}>
                            {getPostcodeDetails(parseInt(postcodeSearch as string))?.postcodeAllStatePenetrationRank}
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </DataTable>
              )}
            </TableContainer>
          </Col>
        )}

        <Col initial={12} laptop={6}>
          <Heading as="h4" variant="h4">
            {description.title}
          </Heading>
          <VSpace initial={4} />
          <p dangerouslySetInnerHTML={{ __html: description.description }} />
        </Col>
        <Col initial={12} laptop={6}>
          {props.downloadData?.mediaItemUrl && props.downloadData?.fileSize && (
            <FileDownload
              title="Download this data"
              file={props.downloadData.mediaItemUrl}
              size={props.downloadData.fileSize}
              underline
            />
          )}
        </Col>
      </div>
    </ChartContainer>
  )
})

const TableContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "lg:absolute lg:top-[calc(var(--spacing-6)+8px)] lg:left-[50px] lg:z-50 lg:w-[500px] lg:max-h-[calc(100%-var(--spacing-6)-var(--spacing-6)-8px-8px)] lg:overflow-auto",
      className,
    )}
    {...props}
  />
))
TableContainer.displayName = "TableContainer"

const DataTable = forwardRef<HTMLTableElement, ComponentPropsWithoutRef<"table">>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn(
      "w-full mb-6 text-center type-body-s table-fixed last-of-type:mb-0",
      "[&_thead]:bg-black [&_thead]:text-white [&_thead]:text-center [&_thead]:type-label-m",
      "[&_thead_th]:relative [&_thead_th]:py-2",
      "[&_thead_th_button]:appearance-none [&_thead_th_button]:border-none [&_thead_th_button]:bg-transparent [&_thead_th_button]:absolute [&_thead_th_button]:top-1/2 [&_thead_th_button]:right-[10px] [&_thead_th_button]:-translate-y-1/2",
      "[&_tbody_tr]:bg-light-grey",
      "[&_tbody_tr_td_div]:flex [&_tbody_tr_td_div]:items-center [&_tbody_tr_td_div]:flex-col [&_tbody_tr_td_div]:py-2",
      "[&_tbody_tr_td_div_i]:flex [&_tbody_tr_td_div_i]:gap-2 [&_tbody_tr_td_div_i]:h-[40px] [&_tbody_tr_td_div_i]:my-2 [&_tbody_tr_td_div_i]:items-center",
      "[&_tbody_tr_td_div_i_svg]:w-[30px] [&_tbody_tr_td_div_i_svg]:h-[30px]",
      "[&_tbody_tr_td_div_i+span]:type-label-s [&_tbody_tr_td_div_i+span]:text-mid-grey [&_tbody_tr_td_div_i+span]:block [&_tbody_tr_td_div_i+span]:w-full",
      "[&_tbody_tr_td_div_i+span+span]:type-label-s [&_tbody_tr_td_div_i+span+span]:mt-1",
      "[&_tfoot]:bg-light-white [&_tfoot]:text-black",
      "[&_tfoot_tr_td]:py-3 [&_tfoot_tr_td]:type-label-s",
      // @bigPhone -> sm:
      "sm:[&_tbody_tr_td_div_i]:h-[60px]",
      "sm:[&_tbody_tr_td_div_i_svg]:w-[40px] sm:[&_tbody_tr_td_div_i_svg]:h-[40px]",
      "sm:[&_tbody_tr_td_div_i+span+span]:type-title-xs",
      className,
    )}
    {...props}
  />
))
DataTable.displayName = "DataTable"

const StyledMapContainer = forwardRef<Map, ComponentProps<typeof MapContainer>>(({ className, ...props }, ref) => (
  <MapContainer ref={ref} className={cn("my-6 w-full h-[60vh] z-10 md:min-h-[600px]", className)} {...props} />
))
StyledMapContainer.displayName = "StyledMapContainer"

const FilterBlock = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-wrap mt-6 rounded-[12px_12px_0_0] overflow-hidden", className)} {...props} />
))
FilterBlock.displayName = "FilterBlock"
